import { Link, NavLink, Outlet } from "react-router-dom";
import {
  Home,
  Users,
  Server,
  FileText,
  Settings as SettingsIcon,
  LogOut,
  ShieldCheck,
  TriangleAlert,
  User,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { beginLogin, getCurrentPrincipal, logout } from "@/api/client";
import { AuthNotice } from "@/auth/AuthScreens";
import { Badge } from "@/components/ui/badge";
import { TenantSwitcher } from "./TenantSwitcher";

interface NavItem {
  to: string;
  label: string;
  icon: typeof Home;
  adminOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: "/admin", label: "Обзор", icon: Home },
  { to: "/admin/vpn-profiles", label: "VPN-профили", icon: Users },
  // Каталог несёт IP релеев и SNI — только платформенному админу (бэкенд с
  // 2026-11-03 отдаёт 403 не-superadmin; демо-организация — посторонние люди).
  { to: "/admin/servers", label: "Серверы", icon: Server, adminOnly: true },
  { to: "/admin/tenants", label: "Организации", icon: ShieldCheck, adminOnly: true },
  { to: "/admin/audit", label: "Audit Log", icon: FileText },
  { to: "/admin/settings", label: "Настройки", icon: SettingsIcon },
];

export function AdminLayout() {
  const principal = getCurrentPrincipal();
  // Токен есть (это гарантирует RequireSession), но не декодируется — битый
  // JWT, а не «нет сессии». Прежний `logout(true)` прямо в теле рендера гасил
  // SSO-сессию всех продуктов (под StrictMode — дважды) и с 0.12 ещё и
  // запирал `startLogin` в этом документе. Побочка уехала в обработчик кнопки.
  if (!principal) return <BrokenSession />;

  const items = NAV.filter((n) => {
    if (n.adminOnly && !principal.is_superadmin) return false;
    return true;
  });

  return (
    <div className="min-h-screen flex">
      <aside className="w-64 border-r border-border bg-bg-surface/60 backdrop-blur flex flex-col">
        <div className="px-5 py-5 border-b border-border">
          <div className="font-display text-lg font-bold tracking-tight">Signaris Net</div>
          <div className="text-xs text-fg-subtle mt-0.5">Admin Console</div>
        </div>

        {principal.is_superadmin && (
          <div className="px-3 pt-3">
            <TenantSwitcher />
          </div>
        )}

        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/admin"}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    isActive
                      ? "bg-accent-subtle text-accent"
                      : "text-fg-muted hover:bg-white/5 hover:text-fg",
                  )
                }
              >
                <Icon size={16} />
                {n.label}
              </NavLink>
            );
          })}

          {/* Tenant_owner — он же сотрудник своего tenant'а: даём
              быстрый доступ к личному кабинету (свои VPN-конфиги, QR). */}
          <Link
            to="/my"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-fg-muted hover:bg-white/5 hover:text-fg transition-colors mt-4"
          >
            <User size={16} />
            Мой VPN-кабинет
          </Link>
        </nav>

        <div className="px-4 py-4 border-t border-border">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="min-w-0">
              <div className="truncate text-sm">{principal.email}</div>
              <div className="mt-1">
                <Badge tone={principal.is_superadmin ? "accent" : "neutral"}>
                  {principal.net_role ?? "—"}
                </Badge>
                {principal.tenant_slug && (
                  <Badge tone="info" className="ml-1">
                    {principal.tenant_slug}
                  </Badge>
                )}
              </div>
            </div>
            <button
              onClick={() => logout(true)}
              className="text-fg-subtle hover:text-fg p-1"
              title="Выйти"
              aria-label="logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function BrokenSession() {
  return (
    <AuthNotice
      icon={<TriangleAlert size={28} className="text-state-danger" />}
      title="Сессия повреждена"
      description="Токен доступа не читается. Нужно войти заново."
      primary={{
        label: "Войти заново",
        onClick: () => {
          // logout(false) — только локальная очистка, без /api/sso/logout:
          // SSO-сессия жива (вход пройдёт без пароля) и, в отличие от
          // authClient.logout(), startLogin в этом документе не запирается.
          void logout(false).then(() =>
            beginLogin("/admin", { force: true }),
          );
        },
      }}
      secondary={{ label: "Выйти", onClick: () => void logout(true) }}
    />
  );
}
